import { BlockId } from '@shared/block-ids';

export enum BiomeType {
  PLAINS = 'plains',
  FOREST = 'forest',
  DESERT = 'desert',
  MOUNTAINS = 'mountains',
  TUNDRA = 'tundra',
  BIRCH_FOREST = 'birch_forest',
  BEACH = 'beach',
  OCEAN = 'ocean',
}

export interface BiomeDef {
  type: BiomeType;
  name: string;
  topBlock: BlockId;
  fillerBlock: BlockId;
  underwaterBlock: BlockId;
  treeDensity: number;
  grassDensity: number;
  flowerDensity: number;
  heightModifier: number;
  color: number;
}

const BIOMES: Record<BiomeType, BiomeDef> = {
  [BiomeType.PLAINS]: {
    type: BiomeType.PLAINS,
    name: 'Plains',
    topBlock: BlockId.GRASS,
    fillerBlock: BlockId.DIRT,
    underwaterBlock: BlockId.SAND,
    treeDensity: 0.02,
    grassDensity: 0.3,
    flowerDensity: 0.05,
    heightModifier: 0,
    color: 0x7cbb4a,
  },
  [BiomeType.FOREST]: {
    type: BiomeType.FOREST,
    name: 'Forest',
    topBlock: BlockId.GRASS,
    fillerBlock: BlockId.DIRT,
    underwaterBlock: BlockId.DIRT,
    treeDensity: 0.15,
    grassDensity: 0.4,
    flowerDensity: 0.03,
    heightModifier: 2,
    color: 0x3d8c26,
  },
  [BiomeType.DESERT]: {
    type: BiomeType.DESERT,
    name: 'Desert',
    topBlock: BlockId.SAND,
    fillerBlock: BlockId.SAND,
    underwaterBlock: BlockId.SAND,
    treeDensity: 0.005,
    grassDensity: 0,
    flowerDensity: 0,
    heightModifier: -2,
    color: 0xd4c45c,
  },
  [BiomeType.MOUNTAINS]: {
    type: BiomeType.MOUNTAINS,
    name: 'Mountains',
    topBlock: BlockId.GRASS,
    fillerBlock: BlockId.STONE,
    underwaterBlock: BlockId.GRAVEL,
    treeDensity: 0.03,
    grassDensity: 0.1,
    flowerDensity: 0.01,
    heightModifier: 20,
    color: 0x888888,
  },
  [BiomeType.TUNDRA]: {
    type: BiomeType.TUNDRA,
    name: 'Tundra',
    topBlock: BlockId.SNOW,
    fillerBlock: BlockId.DIRT,
    underwaterBlock: BlockId.GRAVEL,
    treeDensity: 0.01,
    grassDensity: 0,
    flowerDensity: 0,
    heightModifier: 0,
    color: 0xddddee,
  },
  [BiomeType.BIRCH_FOREST]: {
    type: BiomeType.BIRCH_FOREST,
    name: 'Birch Forest',
    topBlock: BlockId.GRASS,
    fillerBlock: BlockId.DIRT,
    underwaterBlock: BlockId.DIRT,
    treeDensity: 0.12,
    grassDensity: 0.35,
    flowerDensity: 0.08,
    heightModifier: 1,
    color: 0x6daa3f,
  },
  [BiomeType.BEACH]: {
    type: BiomeType.BEACH,
    name: 'Beach',
    topBlock: BlockId.SAND,
    fillerBlock: BlockId.SAND,
    underwaterBlock: BlockId.SAND,
    treeDensity: 0,
    grassDensity: 0,
    flowerDensity: 0,
    heightModifier: -5,
    color: 0xe8d68e,
  },
  [BiomeType.OCEAN]: {
    type: BiomeType.OCEAN,
    name: 'Ocean',
    topBlock: BlockId.SAND,
    fillerBlock: BlockId.SAND,
    underwaterBlock: BlockId.SAND,
    treeDensity: 0,
    grassDensity: 0,
    flowerDensity: 0,
    heightModifier: -15,
    color: 0x2255aa,
  },
};

export function getBiome(temperature: number, humidity: number, height: number): BiomeDef {
  if (height < 58) return BIOMES[BiomeType.OCEAN];
  if (height < 64) return BIOMES[BiomeType.BEACH];
  if (height > 100) return BIOMES[BiomeType.MOUNTAINS];

  if (temperature < 0.3) return BIOMES[BiomeType.TUNDRA];
  if (temperature > 0.7 && humidity < 0.3) return BIOMES[BiomeType.DESERT];
  if (humidity > 0.6) {
    return temperature > 0.5 ? BIOMES[BiomeType.BIRCH_FOREST] : BIOMES[BiomeType.FOREST];
  }
  if (humidity > 0.35) return BIOMES[BiomeType.FOREST];

  return BIOMES[BiomeType.PLAINS];
}

export function getBiomeDef(type: BiomeType): BiomeDef {
  return BIOMES[type];
}
